import ToggleSwitch from '@components/ToggleSwitch';
import { useState, useEffect } from 'react';
import { useGiftItem } from '@hooks/businessContainer';
import { format } from '@monofront/business/utils';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useTranslations } from 'next-intl';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';

interface IGiftItemShoppingCartProps {
  isSideShoppingCart?: boolean;
}

const GiftItemShoppingCart: React.FC<IGiftItemShoppingCartProps> = ({
  isSideShoppingCart = false,
}) => {
  const { shoppingCart } = useShoppingCart();
  const {
    giftItem,
    giftItemInShoppingCart,
    addGiftItemToShoppingCart,
    removeGiftItemFromShoppingCart,
  } = useGiftItem();
  const [isInShoppingCart, setIsInShoppingCart] = useState(false);
  const { formatProductWeight } = useFormatProductWeight();
  const formattedProductWeight = formatProductWeight(giftItem?.meta.finance);

  useEffect(() => {
    const isGiftItemInShoppingCart =
      giftItemInShoppingCart?.id === giftItem?.id;

    setIsInShoppingCart(
      Boolean(shoppingCart?.hasGiftItem && isGiftItemInShoppingCart),
    );
  }, [shoppingCart.hasGiftItem, giftItemInShoppingCart]);

  const updateGiftItemToShoppingCart = async (isAdded: boolean) => {
    setIsInShoppingCart(isAdded);
    isAdded ? addGiftItemToShoppingCart() : removeGiftItemFromShoppingCart();
  };

  const tCatalog = useTranslations('Catalog');

  if (!giftItem) {
    return null;
  }

  return (
    <>
      <div className="container">
        <div className="cart-item-container">
          {giftItem.image && (
            <div className="img">
              <ResponsiveImage
                src={giftItem.image}
                alt={`img-${giftItem.handle}`}
                width={1024}
                height={1024}
              />
            </div>
          )}

          <div className="content">
            <div className="infos">
              <div className="text-base">{giftItem.title}</div>
              {!isSideShoppingCart && (
                <>
                  <p className="vendor">{giftItem.vendor}</p>
                  <p className="weight">{formattedProductWeight}</p>
                </>
              )}
            </div>

            <div className="price">
              {Boolean(giftItem.compare_at_price) && (
                <span className="member-price">
                  {format(giftItem.compare_at_price)}
                </span>
              )}
              <span> {tCatalog('given').toUpperCase()}*</span>
              <div className="details">
                *{tCatalog('onThe')}{' '}
                <span className="bold">{tCatalog('firstOrder')}</span>
              </div>
            </div>

            <div className="toggle">
              <ToggleSwitch
                id="gift"
                name="gititem"
                checked={isInShoppingCart}
                onChange={updateGiftItemToShoppingCart}
                on="1"
                off="0"
              />
            </div>

            {!isSideShoppingCart && (
              <p className="subtotal">{format(giftItem.price)}</p>
            )}
          </div>
        </div>

        {!giftItem.inventory_available && (
          <div className="error">
            <p className="error tw-lf-error-message">
              {tCatalog('outOfStockProduct')}
            </p>
          </div>
        )}
      </div>
      <style jsx>{`
        .container {
          background-color: var(--white);
          padding: var(--space-4) var(--space-4) 0;
        }

        .cart-item-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          padding-bottom: var(--space-4);
          border-bottom: ${isSideShoppingCart
            ? `1px solid var(--clr-tertiary-2)`
            : `none`};
        }

        .img {
          height: 72px;
          width: 72px;
        }

        .content {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: 1fr;
          grid-template-areas: ${isSideShoppingCart
            ? '"infos  toggle" "price  toggle"'
            : '"infos  toggle subtotal"  "price  toggle subtotal"'};
          grid-column-gap: var(--space-6);
          width: 100%;
          align-items: flex-end;
          margin-left: var(--space-4);
        }

        .infos {
          margin-bottom: var(--space-2);
          grid-area: infos;
        }

        .vendor,
        .weight {
          color: var(--clr-grey-1);
        }

        .details {
          font-size: var(--font-size-1);
        }

        .vendor {
          display: none;
        }

        .price {
          grid-area: price;
          font-size: var(--font-size-2);
        }

        .member-price {
          text-decoration: line-through;
          font-family: var(--font-family-1);
          font-size: var(--font-size-2-5);
        }

        .toggle {
          grid-area: toggle;
        }

        .subtotal {
          font-size: var(--font-size-3);
          min-width: 75px;
          grid-area: subtotal;
          justify-self: end;
          text-align: end;
        }

        .error {
          text-align: center;
        }

        @media (max-width: 800px) {
          .subtotal,
          .weight {
            display: none;
          }

          .content {
            grid-column-gap: var(--space-4);
            grid-template-areas:
              'infos infos wish'
              'price toggle toggle';
          }

          .content {
            padding: var(--space-1) 0;
          }

          .title {
            width: 100%;
          }

          .vendor {
            display: block;
          }
        }
      `}</style>
    </>
  );
};

export default GiftItemShoppingCart;
